import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowLeft, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Badge } from '@/components/ui/Badge';
import { toast } from '@/store/toastStore';
import {
  addKbChapterItem,
  deleteKbChapter,
  getKbChapter,
  kbQueryKeys,
  listKnowledgeBaseDocuments,
  removeKbChapterItem,
  reorderKbChapter,
  updateKbChapter,
  type KbChapterItem,
} from '@/services/knowledge-base.service';

export function ChapterEditorPage() {
  const { id } = useParams<{ id: string }>();
  const chapterId = Number(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [showAddDoc, setShowAddDoc] = useState(false);
  const [docSearch, setDocSearch] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState<'active' | 'draft' | 'archived'>('active');
  const [items, setItems] = useState<KbChapterItem[]>([]);

  const chapterQuery = useQuery({
    queryKey: kbQueryKeys.chapter(chapterId),
    queryFn: () => getKbChapter(chapterId),
    enabled: chapterId > 0,
  });

  useEffect(() => {
    if (chapterQuery.data) {
      setTitle(chapterQuery.data.title);
      setDescription(chapterQuery.data.description ?? '');
      setCategory(chapterQuery.data.category ?? '');
      setStatus((chapterQuery.data.status as typeof status) ?? 'active');
      setItems(chapterQuery.data.items);
    }
  }, [chapterQuery.data]);

  const docsQuery = useQuery({
    queryKey: kbQueryKeys.documents({ q: docSearch.trim() || undefined, perPage: 25 }),
    queryFn: () => listKnowledgeBaseDocuments({ q: docSearch.trim() || undefined, perPage: 25 }),
    enabled: showAddDoc,
  });

  const updateMutation = useMutation({
    mutationFn: () => updateKbChapter(chapterId, { title: title.trim(), description, category, status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kbQueryKeys.chapters() });
      queryClient.invalidateQueries({ queryKey: kbQueryKeys.chapter(chapterId) });
      toast.success('Chapter saved');
    },
    onError: () => toast.error('Failed to save chapter'),
  });

  const addItemMutation = useMutation({
    mutationFn: (kbDocumentId: number) => addKbChapterItem(chapterId, { kbDocumentId, required: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kbQueryKeys.chapter(chapterId) });
      toast.success('Document added');
    },
    onError: (err: Error) => toast.error(err.message || 'Could not add'),
  });

  const removeItemMutation = useMutation({
    mutationFn: (itemId: number) => removeKbChapterItem(chapterId, itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kbQueryKeys.chapter(chapterId) });
    },
  });

  const reorderMutation = useMutation({
    mutationFn: (itemIds: number[]) => reorderKbChapter(chapterId, itemIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kbQueryKeys.chapter(chapterId) });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteKbChapter(chapterId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kbQueryKeys.chapters() });
      toast.success('Chapter deleted');
      navigate('..');
    },
    onError: () => toast.error('Failed to delete'),
  });

  function moveItem(index: number, dir: -1 | 1) {
    const next = [...items];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setItems(next);
    reorderMutation.mutate(next.map((i) => i.kbChapterItemId));
  }

  if (chapterQuery.isLoading) {
    return <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-500">Loading...</div>;
  }
  if (!chapterQuery.data) {
    return <div className="rounded-lg border border-slate-200 bg-white p-6">Chapter not found.</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Link to=".." className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700">
          <ArrowLeft className="h-4 w-4" />
          Back to chapters
        </Link>
        <div className="flex gap-2">
          <Button variant="danger" leftIcon={<Trash2 className="h-4 w-4" />} onClick={() => setConfirmDelete(true)}>
            Delete
          </Button>
          <Button onClick={() => updateMutation.mutate()} loading={updateMutation.isPending}>
            Save
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3">
        <Input label="Title" required value={title} onChange={(e) => setTitle(e.target.value)} />
        <div className="grid gap-3 md:grid-cols-2">
          <Input label="Category" value={category} onChange={(e) => setCategory(e.target.value)} />
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as typeof status)}
              className="form-input-base"
            >
              <option value="active">Active</option>
              <option value="draft">Draft</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>
        <Textarea autoResize label="Description" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
      </div>

      <div className="rounded-lg border border-slate-200 bg-white">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2.5">
          <h3 className="text-sm font-semibold text-slate-900">Documents in this chapter</h3>
          <Button size="sm" leftIcon={<Plus className="h-4 w-4" />} onClick={() => setShowAddDoc(true)}>
            Add Document
          </Button>
        </div>

        {items.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-slate-500">
            No documents yet — add the first one to begin building the chapter.
          </div>
        ) : (
          <ol className="divide-y divide-slate-100">
            {items.map((item, idx) => (
              <li key={item.kbChapterItemId} className="flex items-center gap-3 px-4 py-2.5">
                <span className="w-6 text-right text-xs font-semibold text-slate-400">{idx + 1}.</span>
                <div className="min-w-0 flex-1">
                  <Link
                    to={`../../library/${item.kbDocumentId}`}
                    className="text-sm font-medium text-blue-600 hover:underline"
                  >
                    {item.documentTitle}
                  </Link>
                  {item.documentCategory && (
                    <span className="ml-2 text-xs text-slate-500">{item.documentCategory}</span>
                  )}
                  {item.required && (
                    <Badge variant="info" size="sm" className="ml-2">
                      Required
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="xs"
                    aria-label="Move up"
                    disabled={idx === 0}
                    onClick={() => moveItem(idx, -1)}
                  >
                    <ArrowUp className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="xs"
                    aria-label="Move down"
                    disabled={idx === items.length - 1}
                    onClick={() => moveItem(idx, 1)}
                  >
                    <ArrowDown className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="xs"
                    aria-label="Remove"
                    onClick={() => removeItemMutation.mutate(item.kbChapterItemId)}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>

      <Modal isOpen={showAddDoc} onClose={() => setShowAddDoc(false)} title="Add Document" size="lg">
        <div className="space-y-3">
          <Input
            placeholder="Search documents..."
            value={docSearch}
            onChange={(e) => setDocSearch(e.target.value)}
          />
          <div className="max-h-[400px] overflow-y-auto rounded-md border border-slate-200">
            {docsQuery.isLoading ? (
              <div className="p-6 text-center text-sm text-slate-500">Searching...</div>
            ) : !docsQuery.data?.data.length ? (
              <div className="p-6 text-center text-sm text-slate-500">No documents found.</div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {docsQuery.data.data.map((d) => {
                  const alreadyIn = items.some((i) => i.kbDocumentId === d.kbDocumentId);
                  return (
                    <li key={d.kbDocumentId} className="flex items-center justify-between px-3 py-2">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium text-slate-900">{d.title}</div>
                        <div className="text-xs text-slate-500">
                          {d.category || '—'} {d.tags.length > 0 ? `• ${d.tags.join(', ')}` : ''}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant={alreadyIn ? 'secondary' : 'primary'}
                        disabled={alreadyIn || addItemMutation.isPending}
                        onClick={() => addItemMutation.mutate(d.kbDocumentId)}
                      >
                        {alreadyIn ? 'Added' : 'Add'}
                      </Button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => deleteMutation.mutate()}
        title="Delete chapter?"
        message="This removes the chapter and all of its document references. The underlying documents are NOT deleted."
        confirmLabel="Delete"
        variant="danger"
      />
    </div>
  );
}
